import React, { useEffect } from "react";

import {
  useCoveragePriceCalculations,
  useFormState,
} from "Store/priceCalculation/priceCalculationHooks";
import { getMonthlyTffAmount } from "../../../vehicleUtils";
import { CAR_ABOUT_STEP, CAR_FIND_STEP } from "../../carSteps";

import { StyledAlert } from "App/global-style";
import { CurrentPriceMessage } from "Components/CurrentPriceMessage/CurrentPriceMessage";
import { ProductBackendName } from "Components/Features/CheckPrice/Products/productTypes";
import { useTexts } from "Components/TextContext";
import { IsCarTrimmedField } from "Features/CheckPrice/Products/RiskProducts/VehicleProducts/Car/ProductPrice/FormElements/IsCarTrimmedField";
import { TFFInfoBoxMonthly } from "Features/CheckPrice/Products/RiskProducts/VehicleProducts/components/TFFInfoBox/TFFInfoBoxMonthly";
import { useUserIdentification } from "Features/CheckPrice/userIdentificationHook";
import { CarPriceRequestInfo } from "Store/interfaces/products/checkPrice/riskProducts/VehicleProducts/Car";
import { TextLanguage } from "Store/texts/interfaces";
import { useUserIdentificationSelector } from "Store/userIdentification/userIdentificationSelectors";
import { isDefined } from "Utils/functions";
import { isSTBEmployee } from "Utils/isSTBEmployee";
import { useFormikContext } from "formik";
import {
  CarOwner,
  CarProductDetailsFormValues,
} from "../../ProductDetails/FormElements/interfaces";
import { CarProductIntroFormValues } from "../../ProductIntro/FormElements/interfaces";
import { DEFAULT_DEDUCTIBLE_CAR } from "../../carConstants";
import { mapFormToPriceRequest } from "../../utils/mapFormToPriceRequest";
import { priceValidationSchema } from "../carProductPriceValidation";
import { CarPriceFormValues } from "../interfaces";
import { CarOwnerFields } from "./CarOwnerFields";
import { Coverages } from "./Coverages";
import { Deductible } from "./Deductible";
import { ExpectedYearlyUsage } from "./ExpectedYearlyUsage";
import { YoungerDriver } from "./YoungerDriver";
import { CarFormIngressCheckPrice, CarFormIngressCoverages } from "./styled";

interface Props {
  readonly texts: any;
  readonly lang: TextLanguage;
  readonly updatePriceRequest: (
    payload: CarPriceRequestInfo,
    stepForwardOnSuccess?: boolean,
  ) => void;
}

export const FormElements = (props: Props): React.ReactElement => {
  const formProps = useFormikContext<CarPriceFormValues>();
  const { getText } = useTexts();
  const coverages = useCoveragePriceCalculations();
  const { updatePriceRequest, texts, lang } = props;
  const updatePrices = useUpdatePrices(updatePriceRequest);
  const tffAmount = getMonthlyTffAmount(formProps.values.coverage, coverages);
  const hasSelectedCoverage = useHasChosenCoverage();
  const { current: user } = useUserIdentification(ProductBackendName.CAR);
  const isEmployee = isSTBEmployee(user);

  useEffect(() => {
    if (
      !(
        formProps.values.carOwner === CarOwner.PARTNER ||
        formProps.values.carOwner === CarOwner.GUARDIAN
      ) &&
      isDefined(formProps.values.carOwnerAge)
    ) {
      formProps.setFieldValue("carOwnerAge", undefined);
    }
  }, [formProps.values]);

  return (
    <>
      <CarFormIngressCoverages>
        {getText("carform.ingress.coverages")}
      </CarFormIngressCoverages>
      <Coverages updatePrices={updatePrices} />

      {hasSelectedCoverage && (
        <div>
          <CarFormIngressCheckPrice>
            {getText("carform.ingress.checkprice")}
          </CarFormIngressCheckPrice>
          <Deductible texts={texts} lang={lang} />
          <ExpectedYearlyUsage
            updatePrices={updatePrices}
            texts={texts}
            lang={lang}
            isEmployee={isEmployee}
          />
          <CarOwnerFields updatePrices={updatePrices} />
          <YoungerDriver updatePrices={updatePrices} />
          <IsCarTrimmedField />
          {isEmployee && (
            <StyledAlert
              type="success"
              title={getText("carform.alerttitle.frikjorelengdestorebrand")}
            />
          )}
        </div>
      )}
      <CurrentPriceMessage
        productBackendName={ProductBackendName.CAR}
        coverages={coverages}
        selectedPrices={[
          {
            selectedCoverage: formProps.values.coverage,
            selectedDeductible: formProps.values.deductible,
            defaultDeductible: DEFAULT_DEDUCTIBLE_CAR,
          },
        ]}
      />
      <TFFInfoBoxMonthly fee={tffAmount} />
    </>
  );
};

const useHasChosenCoverage = (): boolean => {
  const {
    values: { coverage },
  } = useFormikContext<CarPriceFormValues>();
  return isDefined(coverage);
};

const useUpdatePrices = (
  updatePriceRequest: Props["updatePriceRequest"],
): ((field: string, value: unknown) => void) => {
  const formProps = useFormikContext<CarPriceFormValues>();
  const userIdentification = useUserIdentificationSelector();
  const introFormValues = useFormState(
    CAR_FIND_STEP.id,
  ) as Required<CarProductIntroFormValues>;
  const detailsFormValues = useFormState(
    CAR_ABOUT_STEP.id,
  ) as CarProductDetailsFormValues;

  return (field: string, value: unknown): void => {
    try {
      priceValidationSchema().validateSync({
        ...formProps.values,
        [field]: value,
      });
      updatePriceRequest(
        mapFormToPriceRequest(
          userIdentification,
          introFormValues,
          detailsFormValues,
          { ...formProps.values, [field]: value },
        ),
        false,
      );
    } catch (error) {
      // DO NOTHING
    }
  };
};
